import type { EmailBuilderBlock, EmailBuilderBlockType } from '../api';

const BRAND_COLOR = '#4c9bab';

let idCounter = 0;
const nextId = (): string => {
  idCounter += 1;
  return `blk_${Date.now().toString(36)}_${idCounter}`;
};

export const createDefaultBlock = (type: EmailBuilderBlockType): EmailBuilderBlock => {
  const base = { id: nextId(), type, style: { align: 'left' as const, padding: 12, fontSize: 14, color: '#243746' } };

  switch (type) {
    case 'header':
      return { ...base, text: 'New Heading', style: { ...base.style, fontSize: 24, bold: true, color: BRAND_COLOR } };
    case 'text':
      return { ...base, text: 'Enter your text here.' };
    case 'image':
      return { ...base, src: '', alt: 'Image', widthPercent: 100 };
    case 'button':
      return {
        ...base,
        text: 'Click Here',
        href: 'https://',
        style: { ...base.style, color: '#ffffff', backgroundColor: BRAND_COLOR, align: 'center', padding: 12 },
      };
    case 'table':
      return { ...base, tableRows: [['Header 1', 'Header 2'], ['Cell 1', 'Cell 2']] };
    case 'divider':
      return { ...base, style: { ...base.style, backgroundColor: '#e0d7da', padding: 1 } };
    default:
      return base as EmailBuilderBlock;
  }
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const renderBlock = (block: EmailBuilderBlock): string => {
  const align = block.style.align || 'left';
  const padding = block.style.padding ?? 12;
  const color = block.style.color || '#243746';
  const fontSize = block.style.fontSize ?? 14;
  const fontWeight = block.style.bold ? 700 : 400;

  switch (block.type) {
    case 'header':
      return `<h2 style="margin:0;padding:${padding}px 0;text-align:${align};color:${color};font-size:${fontSize}px;font-weight:${fontWeight};">${escapeHtml(block.text || '')}</h2>`;
    case 'text':
      return `<p style="margin:0;padding:${padding}px 0;text-align:${align};color:${color};font-size:${fontSize}px;font-weight:${fontWeight};">${escapeHtml(block.text || '')}</p>`;
    case 'image':
      return `<div style="padding:${padding}px 0;text-align:${align};"><img src="${escapeHtml(block.src || '')}" alt="${escapeHtml(block.alt || '')}" style="width:${block.widthPercent ?? 100}%;max-width:100%;" /></div>`;
    case 'button': {
      const bg = block.style.backgroundColor || BRAND_COLOR;
      return `<div style="padding:${padding}px 0;text-align:${align};"><a href="${escapeHtml(block.href || '#')}" style="display:inline-block;background:${bg};color:${color};font-size:${fontSize}px;font-weight:${fontWeight};text-decoration:none;padding:10px 20px;border-radius:6px;">${escapeHtml(block.text || '')}</a></div>`;
    }
    case 'table': {
      const rows = block.tableRows || [];
      const rowsHtml = rows
        .map((row, rowIndex) => {
          const cellTag = rowIndex === 0 ? 'th' : 'td';
          const cells = row
            .map(
              (cell) =>
                `<${cellTag} style="border:1px solid #e0d7da;padding:8px;text-align:${align};font-size:${fontSize}px;color:${color};${rowIndex === 0 ? 'background:#eaf4f8;font-weight:700;' : ''}">${escapeHtml(cell)}</${cellTag}>`,
            )
            .join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      return `<div style="padding:${padding}px 0;"><table style="width:100%;border-collapse:collapse;">${rowsHtml}</table></div>`;
    }
    case 'divider':
      return `<hr style="border:none;border-top:${Math.max(1, padding)}px solid ${block.style.backgroundColor || '#e0d7da'};margin:8px 0;" />`;
    default:
      return '';
  }
};

// Renders the block list into the same wrap()-style HTML shell used by the
// system default templates (constants/email-events.js on the backend) so
// custom templates look consistent with the rest of the app's emails.
export const blocksToHtml = (blocks: EmailBuilderBlock[]): string => {
  const inner = blocks.map(renderBlock).join('\n');
  return `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;color:#243746;">
${inner}
  <p style="color:#8a99a3;font-size:12px;margin-top:24px;">This is an automated notification from the Swagpro Medical Billing Dashboard.</p>
</div>`;
};

export const BLOCK_TYPE_LABELS: Record<EmailBuilderBlockType, string> = {
  header: 'Header',
  text: 'Text',
  image: 'Image',
  button: 'Button',
  table: 'Table',
  divider: 'Divider',
};
